import React, { useState } from 'react';
import { theme, btnPrimary, btnSecondary } from '../theme';
// v0.26.0 — Owner identity now comes from the logged-in user, not
// from sticky-localStorage form fields. The old droppin.owner.identity.v1
// key is ignored (left in localStorage harmlessly; no read site).
import { getCurrentUser } from '../utils/user';

/**
 * NewReportModal — v0.23.0 / v0.25.0 / v0.26.0.
 *
 * Asks the operator to name a new report BEFORE the POST happens.
 * Owner is the logged-in user (read from session); no manual owner
 * fields any more.
 *
 * Props:
 *   onCancel()                — close without creating
 *   onCreate({ name, description, seedMembers }) — async. The caller
 *                                                  POSTs and awaits.
 */
export default function NewReportModal({ onCancel, onCreate, projectName, projectId }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // v0.339.0 — Brett: "Location shows nothing. Do it properly." A new
  // report starts with its creator alone (the server places them); its Team
  // box offers "Copy the team from <latest report>" in one press. The old
  // "Import project team" tickbox cloned a location-wide list silently, ON by
  // default, so it is gone.

  // v0.26.0 — Owner = the currently signed-in user. AppShell gates
  // the whole app on sign-in so this should always be populated, but
  // we still defensively handle the null case below.
  const me = getCurrentUser();
  const ownerSeed = me ? [{
    name:  me.name || me.username || 'Unknown',
    email: me.email || '',
    role:  me.role  || '',
  }] : [];

  async function submit(e) {
    e?.preventDefault();
    const clean = name.trim();
    if (!clean) { setErr('Please give the report a name.'); return; }
    setBusy(true); setErr(null);
    try {
      const seedMembers = ownerSeed;
      await onCreate({
        name: clean,
        description: description.trim(),
        seedMembers,
      });
    } catch (e2) {
      setErr(e2.message || 'Could not create report');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={backdrop} onClick={onCancel}>
      <form onSubmit={submit} style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 11, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>New report</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginTop: 2 }}>
            {projectName ? `for ${projectName}` : 'Create a report'}
          </div>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: theme.textLight }}>
            Report name
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='e.g. "Pre-handover walkthrough 27 May"'
              style={input}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: theme.textLight }}>
            Description (optional)
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What's this report for?"
              style={input}
            />
          </label>

          {/* v0.26.0 — Owner is the signed-in user (read-only pill).
              Profile fields are managed in InSpec Admin. The rest of
              the project team can be imported with one click after the
              report opens. */}
          {me && (
            <div style={{
              marginTop: 6, padding: '8px 10px',
              border: `1px solid ${theme.border}`, borderRadius: 6,
              background: theme.bg,
              display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
            }}>
              <span style={{
                width: 26, height: 26, borderRadius: '50%', background: theme.brand,
                color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
              }}>{(me.name || me.username || '?').trim().charAt(0).toUpperCase()}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: theme.text }}>{me.name || me.username}</div>
                <div style={{ color: theme.textLight, fontSize: 11 }}>
                  Report owner{me.role ? ` · ${me.role}` : ''}{me.email ? ` · ${me.email}` : ''}
                </div>
              </div>
            </div>
          )}

          <div data-part="new-report-team-note" style={{ fontSize: 12, color: theme.textLight, lineHeight: 1.45 }}>
            Only you are on this report's team to start with. Its Team box offers to copy the team from another report here in one press.
          </div>

          {err && <div style={{ color: theme.danger, fontSize: 12 }}>{err}</div>}
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onCancel} disabled={busy} style={btnSecondary}>Cancel</button>
          <button type="submit" disabled={busy || !name.trim()} style={{ ...btnPrimary, opacity: busy || !name.trim() ? 0.6 : 1 }}>
            {busy ? 'Creating…' : 'Create report'}
          </button>
        </div>
      </form>
    </div>
  );
}

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const panel = {
  width: 'min(460px, 92vw)', background: theme.surface, borderRadius: 12,
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column',
};
const input = {
  padding: '8px 10px', fontSize: 13, border: `1px solid ${theme.border}`,
  borderRadius: 6, background: theme.surface, color: theme.text, outline: 'none',
};
